"use client";
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import Image from "next/image";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { m, AnimatePresence } from "framer-motion";

interface Testimonial {
  quote: string;
  name: string;
  designation: string;
  src: string;
}
interface Colors {
  name?: string;
  designation?: string;
  testimony?: string;
  arrowBackground?: string;
  arrowForeground?: string;
  arrowHoverBackground?: string;
}
interface FontSizes {
  name?: string;
  designation?: string;
  quote?: string;
}
interface CircularTestimonialsProps {
  testimonials: Testimonial[];
  autoplay?: boolean;
  colors?: Colors;
  fontSizes?: FontSizes;
}

export const CircularTestimonials = ({
  testimonials,
  autoplay = true,
  colors = {},
  fontSizes = {},
}: CircularTestimonialsProps) => {
  // Color & font config
  const colorName = colors.name ?? "#000";
  const colorDesignation = colors.designation ?? "#6b7280";
  const colorTestimony = colors.testimony ?? "#4b5563";
  const colorArrowBg = colors.arrowBackground ?? "#141414";
  const colorArrowFg = colors.arrowForeground ?? "#f1f1f7";
  const colorArrowHoverBg = colors.arrowHoverBackground ?? "#00a6fb";
  const fontSizeName = fontSizes.name ?? "1.5rem";
  const fontSizeDesignation = fontSizes.designation ?? "0.925rem";
  const fontSizeQuote = fontSizes.quote ?? "1.125rem";

  // State
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);

  // Bumped on every manual navigation. The autoplay effect depends on it, so a click
  // RESTARTS the 5s timer rather than killing it — previously the handlers called
  // clearInterval with nothing to restart it, so one tap on an arrow stopped rotation
  // for the rest of the session and the visitor saw a single frozen testimonial.
  const [interactionCount, setInteractionCount] = useState(0);

  const testimonialsLength = useMemo(() => testimonials.length, [testimonials]);
  const activeTestimonial = useMemo(
    () => testimonials[activeIndex],
    [activeIndex, testimonials]
  );

  // Autoplay
  useEffect(() => {
    if (!autoplay) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonialsLength);
    }, 5000);
    return () => clearInterval(timer);
    // interactionCount: a manual prev/next re-runs this effect, which tears down the old
    // timer and starts a fresh 5s window from the slide the visitor chose.
  }, [autoplay, testimonialsLength, interactionCount]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line
  }, [activeIndex, testimonialsLength]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % testimonialsLength);
    setInteractionCount((c) => c + 1);
  }, [testimonialsLength]);
  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + testimonialsLength) % testimonialsLength);
    setInteractionCount((c) => c + 1);
  }, [testimonialsLength]);

  // Framer Motion variants for quote
  const quoteVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <div className="w-full max-w-md px-8 py-4 mx-auto">
      <div className="flex flex-col items-center text-center">
        {/* Content */}
        <AnimatePresence mode="wait">
          <m.div
            key={activeIndex}
            variants={quoteVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full"
          >
            <h3
              className="font-bold mb-1 font-NeueMontreal"
              style={{ color: colorName, fontSize: fontSizeName }}
            >
              {activeTestimonial.name}
            </h3>
            <p
              className="mb-4 !font-Schibsted_Grotesk"
              style={{ color: colorDesignation, fontSize: fontSizeDesignation }}
            >
              {activeTestimonial.designation}
            </p>
            <m.p
              className="leading-relaxed !font-Schibsted_Grotesk"
              style={{ color: colorTestimony, fontSize: fontSizeQuote }}
            >
              {activeTestimonial.quote.split(" ").map((word, i) => (
                <m.span
                  key={i}
                  initial={{
                    filter: "blur(10px)",
                    opacity: 0,
                    y: 5,
                  }}
                  animate={{
                    filter: "blur(0px)",
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.22,
                    ease: "easeInOut",
                    delay: 0.025 * i,
                  }}
                  style={{ display: "inline-block" }}
                >
                  {word}&nbsp;
                </m.span>
              ))}
            </m.p>
          </m.div>
        </AnimatePresence>

        {/* Single small circular avatar for the active testimonial */}
        <div className="relative w-16 h-16 mt-6 rounded-full overflow-hidden">
          <AnimatePresence mode="wait">
            <m.div
              key={activeIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full"
              style={{ border: "2px solid #0D318E" }}
            >
              <Image
                src={activeTestimonial.src}
                alt={activeTestimonial.name}
                fill
                sizes="64px"
                className="object-cover rounded-full"
              />
            </m.div>
          </AnimatePresence>
        </div>

        <div
          className="flex items-center justify-center rounded-full pt-6"
          style={{ gap: 2 }}
        >
          <button
            className="w-11 h-11 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 border-2"
            onClick={handlePrev}
            style={{
              borderColor: colorArrowBg,
              backgroundColor: hoverPrev ? colorArrowHoverBg : "transparent",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
            onMouseEnter={() => setHoverPrev(true)}
            onMouseLeave={() => setHoverPrev(false)}
            aria-label="Previous testimonial"
          >
            <FaArrowLeft
              size={14}
              color={hoverPrev ? colorArrowFg : colorArrowBg}
            />
          </button>
          <button
            className="w-11 h-11 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 border-2"
            onClick={handleNext}
            style={{
              borderColor: colorArrowBg,
              backgroundColor: hoverNext ? colorArrowHoverBg : "transparent",
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
            onMouseEnter={() => setHoverNext(true)}
            onMouseLeave={() => setHoverNext(false)}
            aria-label="Next testimonial"
          >
            <FaArrowRight
              size={14}
              color={hoverNext ? colorArrowFg : colorArrowBg}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CircularTestimonials;
